import React from "react";
import {
  EuiButton,
  EuiCheckbox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiSelectable,
  EuiSelectableOption,
  EuiSpacer,
  useEuiTheme,
} from "@elastic/eui";
import Popover from "components/Popover/Popover";
import Button from "components/Buttons/Button";
import Badge from "components/Badge/Badge";

export type SelectablePopoverOption = EuiSelectableOption & {
  key: string;
  secondaryValue?: string;
  resourceType?: string;
  [key: string]: any;
};

interface SelectablePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onButtonClick: () => void;
  onApply: () => void;
  options: SelectablePopoverOption[];
  onOptionsChange: (options: SelectablePopoverOption[]) => void;
  selectAll: boolean;
  onSelectAllChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  buttonText: string;
  searchPlaceholder: string;
  columnHeaders: [string, string, string];
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  panelWidth?: string;
  showSearch?: boolean;
  showHeaders?: boolean;
  buttonTextColor?: string;
  buttonBackgroundColor?: string;
}

import { createUseStyles } from "react-jss";

export const useSelectablePopoverStyles = createUseStyles({
  container: {
    "& .euiSelectableListItem__iconCheck": {
      display: "none !important",
    },
    "& .euiSelectableListItem__content--selected::after": {
      display: "none !important",
    },
    "& .euiSelectableListItem__prepend": {
      display: "none !important",
    },
    "& .euiSelectableListItem__append": {
      display: "none !important",
    },
    "& .euiFieldSearch": {
      boxShadow: "none",
      borderBottom: "1px solid var(--border-color)",
      borderRadius: "6px 6px 0 0",
    },
    "& .euiFieldSearch--compressed": {
      boxShadow: "none",
    },
    "& .euiSelectableListItem__text.euiSelectableListItem__text--truncate": {
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      display: "block",
      width: "100%",
      flex: 1,
    },
  },
  columnHeader: {
    paddingLeft: "30px",
    display: "grid",
    gridTemplateColumns: "5fr 2fr 5fr",
    padding: "8px",
    borderBottom: "1px solid var(--border-color)",
    backgroundColor: "var(--table-header-background)",
    fontWeight: 400,
    "& span:first-child": {
      paddingLeft: "12px",
    },
    "& span:nth-child(2)": {
      marginLeft: "-2px",
    },
  },
  optionRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
    "& > *": {
      width: "100%",
    },
    "& label": {
      width: "100%",
    },
  },
  checkboxLabel: {
    display: "grid",
    gridTemplateColumns: "5fr 2fr 5fr",
    width: "100%",
    alignItems: "center",
    paddingLeft: "8px",
    gap: "8px",
    "& > span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& > span:nth-child(2)": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  selectButton: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    "& > span": {
      justifyContent: "space-between",
    },
    "& .euiButton__content": {
      textDecoration: "none !important",
    },
    "& span": {
      textDecoration: "none !important",
    },
  },
});

const SelectablePopover: React.FC<SelectablePopoverProps> = ({
  isOpen,
  onClose,
  onButtonClick,
  onApply,
  options,
  onOptionsChange,
  selectAll,
  onSelectAllChange,
  buttonText,
  searchPlaceholder,
  columnHeaders,
  isLoading = false,
  disabled = false,
  className = "",
  panelWidth = "950px",
  showSearch = true,
  showHeaders = true,
  buttonTextColor = "var(--text)",
  buttonBackgroundColor = "var(--input-bg)",
}) => {
  const theme = useEuiTheme();
  const classes = useSelectablePopoverStyles();

  const selectableProps = showSearch
    ? {
        singleSelection: false as const,
        isLoading,
        listProps: { showIcons: false },
        searchable: true as const,
        searchProps: { placeholder: searchPlaceholder },
        options,
        onChange: onOptionsChange,
      }
    : {
        singleSelection: false as const,
        isLoading,
        listProps: { showIcons: false },
        searchable: false as const,
        options,
        onChange: onOptionsChange,
      };

  return (
    <EuiFormRow fullWidth>
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        anchorPosition="downLeft"
        panelPaddingSize="none"
        panelStyle={{ width: panelWidth }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        button={
          <EuiButton
            className={className}
            fullWidth
            style={{
              backgroundColor: buttonBackgroundColor,
              // border: "1px solid var(--border-color)",
              boxShadow: `inset 0 0 0 1px ${theme?.colorMode === "DARK" ? "#485975" : "#CAD3E2"}`,
              color: buttonTextColor,
              fontWeight: 400,
              borderRadius: "10px",
            }}
            iconType="arrowDown"
            iconSide="right"
            onClick={onButtonClick}
            disabled={disabled}>
            {buttonText}
          </EuiButton>
        }
        children={
          <>
            <div className={classes.container}>
              <EuiSelectable
                {...selectableProps}
                searchProps={{
                  placeholder: "",
                  compressed: false,
                  css: {
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    borderBottom: ".6px solid var(--border-color)",
                    boxShadow: "none",
                    backgroundColor: "var(--card-background)",
                  },
                }}
                renderOption={(option) => {
                  const customOption = option as SelectablePopoverOption;
                  const isChecked = customOption.checked === "on";

                  const handleToggle = () => {
                    const updatedOptions = options.map((opt) =>
                      opt.key === customOption.key
                        ? { ...opt, checked: isChecked ? undefined : "on" }
                        : opt
                    );
                    // @ts-ignore
                    onOptionsChange(updatedOptions);
                  };

                  return (
                    <div
                      className={classes.optionRow}
                      onClick={handleToggle}
                      style={{ cursor: disabled ? "default" : "pointer" }}>
                      <EuiCheckbox
                        id={`checkbox_${customOption.key}`}
                        checked={isChecked}
                        onChange={handleToggle}
                        onClick={(e) => e.stopPropagation()}
                        disabled={disabled}
                        label={
                          <div
                            className={classes.checkboxLabel}
                            onClick={(e) => e.stopPropagation()}>
                            <span>{customOption.label}</span>
                            {customOption?.resourceType && (
                              <span>
                                <Badge
                                  text={customOption?.resourceType || ""}
                                  style={{ borderRadius: "4px" }}
                                />
                              </span>
                            )}
                            <span>{customOption?.secondaryValue || ""}</span>
                          </div>
                        }
                      />
                    </div>
                  );
                }}>
                {(list, search) => (
                  <div style={{ width: "100%" }}>
                    {showSearch && search}
                    {showHeaders && (
                      <div className={classes.columnHeader}>
                        <span style={{ fontWeight: 600 }}>{columnHeaders[0]}</span>
                        <span style={{ fontWeight: 600 }}>{columnHeaders[1]}</span>
                        <span style={{ fontWeight: 600 }}>{columnHeaders[2]}</span>
                      </div>
                    )}
                    <div
                      style={{
                        padding: "4px",
                        paddingLeft: "12px",
                        borderBottom: "1px solid var(--border-color)",
                        cursor: disabled ? "default" : "pointer",
                      }}>
                      <EuiCheckbox
                        id="select-all-checkbox"
                        label={<span style={{ paddingLeft: "8px" }}>Select All</span>}
                        checked={selectAll}
                        onChange={onSelectAllChange}
                        disabled={disabled}
                      />
                    </div>
                    {list}
                  </div>
                )}
              </EuiSelectable>
            </div>
            <EuiSpacer size="s" />
            <EuiFlexGroup
              gutterSize="m"
              justifyContent="flexEnd"
              style={{
                padding: "16px",
                backgroundColor: "var(--app-background)",
                borderTop: "1px solid var(--border-color)",
                borderRadius: "0 0 10px 10px",
              }}>
              <EuiFlexItem grow={false}>
                <Button type="secondary" text="Cancel" onClick={onClose} disabled={disabled} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <Button type="primary" text="Apply" onClick={onApply} disabled={disabled} />
              </EuiFlexItem>
            </EuiFlexGroup>
          </>
        }
      />
    </EuiFormRow>
  );
};

export default SelectablePopover;
